import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import { fmtDateTime } from '../../../utils/ruDates';

const emptyForm = {
    job_key: '',
    title: '',
    job_type: '',
    mode_default: 'apply',
    enabled: true,
    schedule_type: 'daily',
    schedule_hour: 3,
    schedule_minute: 0,
    payload_template_text: '{\n  "action": "sync_api_tools"\n}',
};

function AdminSchedules() {
    const { request } = useApi();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [runningId, setRunningId] = useState('');
    const [deletingId, setDeletingId] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [error, setError] = useState('');
    const [form, setForm] = useState(emptyForm);

    const selectedItem = useMemo(
        () => items.find((item) => item.id === selectedId) || null,
        [items, selectedId]
    );

    useEffect(() => {
        loadSchedules();
    }, []);

    const loadSchedules = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await request('/api/v1/admin/schedules');
            const nextItems = data?.items || [];
            setItems(nextItems);
            if (!selectedId && nextItems.length > 0) {
                openItem(nextItems[0]);
            } else if (selectedId) {
                const current = nextItems.find((item) => item.id === selectedId);
                if (current) openItem(current);
            }
        } catch (err) {
            setError(err?.message || 'Не удалось загрузить расписания');
        } finally {
            setLoading(false);
        }
    };

    const openItem = (item) => {
        setSelectedId(item.id);
        setForm({
            job_key: item.job_key || '',
            title: item.title || '',
            job_type: item.job_type || '',
            mode_default: item.mode_default || 'apply',
            enabled: Boolean(item.enabled),
            schedule_type: item.schedule_type || 'daily',
            schedule_hour: item.schedule_hour ?? 3,
            schedule_minute: item.schedule_minute ?? 0,
            payload_template_text: JSON.stringify(item.payload_template || {}, null, 2),
        });
    };

    const resetForm = () => {
        setSelectedId('');
        setForm(emptyForm);
        setError('');
    };

    const saveSchedule = async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                job_key: form.job_key.trim(),
                title: form.title.trim(),
                job_type: form.job_type.trim(),
                mode_default: form.mode_default,
                enabled: form.enabled,
                schedule_type: form.schedule_type,
                schedule_hour: Number(form.schedule_hour),
                schedule_minute: Number(form.schedule_minute),
                payload_template: form.payload_template_text.trim()
                    ? JSON.parse(form.payload_template_text)
                    : {},
            };
            await request('/api/v1/admin/schedules', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            await loadSchedules();
        } catch (err) {
            setError(err?.message || 'Не удалось сохранить расписание');
        } finally {
            setSaving(false);
        }
    };

    const runNow = async (item) => {
        setRunningId(item.id);
        setError('');
        try {
            await request(`/api/v1/admin/schedules/${item.id}/run`, { method: 'POST' });
            await loadSchedules();
        } catch (err) {
            setError(err?.message || 'Не удалось запустить расписание');
        } finally {
            setRunningId('');
        }
    };

    const deleteItem = async (item) => {
        if (!window.confirm('Удалить расписание? Задача перестанет запускаться.')) return;
        setDeletingId(item.id);
        setError('');
        try {
            await request(`/api/v1/admin/schedules/${item.id}`, { method: 'DELETE' });
            await loadSchedules();
            if (selectedId === item.id) {
                resetForm();
            }
        } catch (err) {
            setError(err?.message || 'Не удалось удалить расписание');
        } finally {
            setDeletingId('');
        }
    };

    const formatDateTime = (value) => (value ? fmtDateTime(value) : '—');

    return (
        <div className="admin-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h1>Расписания (schedules)</h1>
                    <p style={{ color: '#666', margin: '6px 0 0' }}>
                        Правила запуска фоновых задач. Здесь задается периодичность и payload по умолчанию.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={resetForm}>Новое расписание</button>
                    <button onClick={loadSchedules}>Обновить</button>
                </div>
            </div>

            {error ? (
                <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#fff1f0', color: '#a8071a' }}>
                    {error}
                </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: 20, alignItems: 'start' }}>
                <section style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <div style={{ padding: 16, borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>
                        Список расписаний
                    </div>
                    <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
                        {loading && items.length === 0 ? (
                            <div style={{ padding: 16 }}>Загрузка...</div>
                        ) : null}
                        {items.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    padding: 16,
                                    borderBottom: '1px solid #f5f5f5',
                                    background: item.id === selectedId ? '#f5f8ff' : '#fff',
                                }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>{item.title}</div>
                                <div style={{ color: '#666', fontSize: 13, marginBottom: 8 }}>{item.job_key}</div>
                                <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                                    {item.enabled ? 'Включено' : 'Выключено'} · {item.schedule_type} · {String(item.schedule_hour).padStart(2, '0')}:{String(item.schedule_minute).padStart(2, '0')}
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <button onClick={() => openItem(item)}>Открыть</button>
                                    <button onClick={() => runNow(item)} disabled={runningId === item.id}>
                                        {runningId === item.id ? 'Запуск...' : 'Запустить сейчас'}
                                    </button>
                                    <button
                                        onClick={() => deleteItem(item)}
                                        disabled={deletingId === item.id || item.job_key === 'api_tools_inventory_sync'}
                                    >
                                        {deletingId === item.id ? 'Удаление...' : 'Удалить'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: 20 }}>
                    <h2 style={{ marginTop: 0 }}>Редактирование расписания</h2>
                    {selectedItem ? (
                        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
                            Последний запуск: {formatDateTime(selectedItem.last_run_at)}<br />
                            Следующий запуск: {formatDateTime(selectedItem.next_run_at)}<br />
                            Последний статус: {selectedItem.last_status || '—'}
                        </div>
                    ) : (
                        <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
                            Создание нового расписания.
                        </div>
                    )}

                    <div style={{ display: 'grid', gap: 12 }}>
                        <input
                            placeholder="job_key"
                            value={form.job_key}
                            onChange={(e) => setForm((prev) => ({ ...prev, job_key: e.target.value }))}
                        />
                        <input
                            placeholder="title"
                            value={form.title}
                            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                        />
                        <input
                            placeholder="job_type"
                            value={form.job_type}
                            onChange={(e) => setForm((prev) => ({ ...prev, job_type: e.target.value }))}
                        />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <select
                                value={form.mode_default}
                                onChange={(e) => setForm((prev) => ({ ...prev, mode_default: e.target.value }))}
                            >
                                <option value="dry_run">dry_run</option>
                                <option value="apply">apply</option>
                            </select>
                            <select
                                value={form.schedule_type}
                                onChange={(e) => setForm((prev) => ({ ...prev, schedule_type: e.target.value }))}
                            >
                                <option value="daily">daily</option>
                            </select>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <input
                                type="number"
                                min="0"
                                max="23"
                                placeholder="schedule_hour"
                                value={form.schedule_hour}
                                onChange={(e) => setForm((prev) => ({ ...prev, schedule_hour: e.target.value }))}
                            />
                            <input
                                type="number"
                                min="0"
                                max="59"
                                placeholder="schedule_minute"
                                value={form.schedule_minute}
                                onChange={(e) => setForm((prev) => ({ ...prev, schedule_minute: e.target.value }))}
                            />
                        </div>

                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                                type="checkbox"
                                checked={form.enabled}
                                onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                            />
                            Включено
                        </label>

                        <textarea
                            rows={14}
                            placeholder="payload_template JSON"
                            value={form.payload_template_text}
                            onChange={(e) => setForm((prev) => ({ ...prev, payload_template_text: e.target.value }))}
                        />

                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={saveSchedule} disabled={saving}>
                                {saving ? 'Сохранение...' : 'Сохранить'}
                            </button>
                            <button onClick={resetForm}>Очистить форму</button>
                            {selectedItem ? (
                                <button onClick={() => runNow(selectedItem)} disabled={runningId === selectedItem.id}>
                                    {runningId === selectedItem.id ? 'Запуск...' : 'Запустить сейчас'}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default AdminSchedules;
